import {
  Badge,
  ComboboxItem,
  Group,
  MultiSelect,
  OptionsFilter,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconFolder, IconPoint } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getDataTreeForm } from "../../api/ApiMegaMenu";
import { API_ROUTE } from "../../const/apiRoute";
import { convertImageDomain } from "../../const/converhandle";
import { api } from "../../library/axios";
import { CategoryTree } from "../../model/TblCategoryCommand";

export type FlatCategory = {
  id: number;
  value: string;
  name: string;
  path: string;
  level: number;
  hasChildren: boolean;
  searchKey: string;
};

// Bỏ dấu tiếng Việt để gõ "hdmi", "thiet bi" vẫn tìm ra
export const toSearchKey = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const TREE_QUERY = "?CategoryType=category&IsTradeIn=N&Skip=0&Take=2000";

// Tải cây danh mục một lần cho cả màn; lỗi thì cho tải lại ở lần sau
let treeRequest: Promise<FlatCategory[] | null> | null = null;

const flatten = (nodes: CategoryTree[], level = 0, parentPath = ""): FlatCategory[] =>
  nodes.flatMap((node) => {
    const name = (node.categoryName ?? "").trim() || `Danh mục ${node.id}`;
    const path = parentPath ? `${parentPath} > ${name}` : name;
    const children = node.categoryChildModels ?? [];
    return [
      {
        id: node.id,
        value: String(node.id),
        name,
        path,
        level,
        hasChildren: children.length > 0,
        searchKey: toSearchKey(path),
      },
      ...flatten(children, level + 1, path),
    ];
  });

const loadCategories = () => {
  if (!treeRequest) {
    treeRequest = getDataTreeForm(TREE_QUERY)
      .then((res: any) => {
        const tree = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : null;
        if (!tree) {
          treeRequest = null;
          return null;
        }
        return flatten(tree as CategoryTree[]);
      })
      .catch(() => {
        treeRequest = null;
        return null;
      });
  }
  return treeRequest;
};

export const useCategoryTree = () => {
  const [nodes, setNodes] = useState<FlatCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    loadCategories().then((result) => {
      if (!alive) return;
      setNodes(result ?? []);
      setFailed(result === null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const byValue = useMemo(() => new Map(nodes.map((n) => [n.value, n])), [nodes]);
  return { nodes, byValue, loading, failed };
};

// Ảnh của danh mục lấy từ API chi tiết (màn sửa danh mục): ưu tiên ảnh đại diện (imgBig), rồi ảnh icon (imgurl).
// Gọi thẳng api để lỗi 401 không bật thông báo cho từng ô; mỗi id chỉ gọi một lần.
const imageRequests = new Map<number, Promise<string | null>>();

const CATEGORY_IMAGE_CDN = "https://cdn-files.hacom.vn/hacom/cdn/web/media/category/";

const loadCategoryImage = (id: number) => {
  let request = imageRequests.get(id);
  if (!request) {
    request = api
      .get(`${API_ROUTE.MODIFY_CATEGORY}?id=${id}`)
      .then((res) => {
        const data = res.data?.data;
        const url: unknown = data?.imgBig || data?.imgurl;
        if (typeof url !== "string" || !url.trim()) return null;
        if (/^https?:\/\//i.test(url)) return convertImageDomain(url);
        // Chỉ lưu tên file: ghép thư mục ảnh danh mục trên CDN, giống màn Sửa danh mục
        return `${CATEGORY_IMAGE_CDN}${url.trim().replace(/^\/+/, "")}`;
      })
      .catch(() => {
        imageRequests.delete(id);
        return null;
      });
    imageRequests.set(id, request);
  }
  return request;
};

export const useCategoryImage = (id: number | null) => {
  const [loaded, setLoaded] = useState<{ id: number; url: string | null } | null>(null);

  useEffect(() => {
    if (id === null) return;
    let alive = true;
    loadCategoryImage(id).then((url) => alive && setLoaded({ id, url }));
    return () => {
      alive = false;
    };
  }, [id]);

  return loaded && loaded.id === id ? loaded.url : null;
};

const normalize = (text: string) => text.toLowerCase().replace(/\s+/g, " ").trim();

// Khớp tên danh mục trong dữ liệu mẫu ("Laptop > Laptop Gaming" hoặc "Laptop Gaming") với danh mục thật
export const matchCategory = (nodes: FlatCategory[], name?: string | null) => {
  if (!name) return undefined;
  const wanted = normalize(name);
  const byPath = nodes.find((n) => normalize(n.path) === wanted);
  if (byPath) return byPath;
  const leaf = normalize(name.split(">").pop() ?? name);
  return nodes.find((n) => normalize(n.name) === leaf);
};

const renderTreeOption = (node: FlatCategory | undefined, label: string, checked?: boolean) => (
  <Group gap={6} wrap="nowrap" style={{ paddingLeft: (node?.level ?? 0) * 18, width: "100%" }}>
    {node?.hasChildren ? (
      <IconFolder size={14} color="var(--mantine-color-gray-6)" aria-hidden />
    ) : (
      <IconPoint size={14} color="var(--mantine-color-gray-5)" aria-hidden />
    )}
    <Text fz={14} fw={node && node.level === 0 ? 600 : 400} style={{ flex: "1 1 auto" }}>
      {node?.name ?? label}
    </Text>
    {checked && <IconCheck size={14} aria-hidden />}
  </Group>
);

const levelLabel = (node: FlatCategory) => `Cấp ${node.level + 1}`;

// Ô chỉ hiện tên danh mục cuối, nhưng gõ tìm vẫn khớp theo cả đường dẫn cha (không dấu)
const pathFilter =
  (byValue: Map<string, FlatCategory>): OptionsFilter =>
  ({ options, search }) => {
    const q = toSearchKey(search.trim());
    if (!q) return options;
    return options.filter((o) => {
      const item = o as ComboboxItem;
      return byValue.get(item.value)?.searchKey.includes(q) ?? false;
    });
  };

const statusPlaceholder = (loading: boolean, failed: boolean, fallback?: string) => {
  if (loading) return "Đang tải danh mục...";
  if (failed) return "Không tải được danh mục";
  return fallback ? `Chưa khớp: ${fallback}` : "Chọn danh mục";
};

type CategoryTreeSelectProps = {
  value: number | null;
  onChange: (id: number | null, node?: FlatCategory) => void;
  fallbackName?: string | null;
  label?: string;
  ariaLabel?: string;
};

export const CategoryTreeSelect = ({
  value,
  onChange,
  fallbackName,
  label,
  ariaLabel,
}: CategoryTreeSelectProps) => {
  const { nodes, byValue, loading, failed } = useCategoryTree();
  const matchedRef = useRef(false);

  useEffect(() => {
    if (matchedRef.current || loading || value !== null || !fallbackName) return;
    matchedRef.current = true;
    const found = matchCategory(nodes, fallbackName);
    if (found) onChange(found.id, found);
  }, [loading, nodes, value, fallbackName, onChange]);

  const selected = value === null ? undefined : byValue.get(String(value));

  return (
    <Tooltip
      label={selected?.path}
      disabled={!selected || selected.level === 0}
      position="top-start"
      openDelay={300}
      multiline
      maw={420}
      withinPortal
    >
      <Select
        label={label}
        aria-label={ariaLabel}
        placeholder={statusPlaceholder(loading, failed, fallbackName ?? undefined)}
        data={nodes.map((n) => ({ value: n.value, label: n.name }))}
        filter={pathFilter(byValue)}
        value={value === null ? null : String(value)}
        onChange={(v) => {
          const node = v ? byValue.get(v) : undefined;
          onChange(node ? node.id : null, node);
        }}
        renderOption={({ option, checked }) =>
          renderTreeOption(byValue.get(option.value), option.label, checked)
        }
        leftSection={
          selected ? (
            <Badge size="xs" variant="light" color="blue" radius="sm" tt="none">
              {levelLabel(selected)}
            </Badge>
          ) : undefined
        }
        leftSectionWidth={selected ? 62 : undefined}
        searchable
        clearable
        disabled={loading}
        error={failed ? "Không gọi được API danh mục, thử tải lại trang" : undefined}
        nothingFoundMessage="Không có danh mục khớp"
        maxDropdownHeight={320}
        comboboxProps={{ withinPortal: true, keepMounted: false }}
      />
    </Tooltip>
  );
};

type CategoryTreeMultiSelectProps = {
  value: number[];
  onChange: (ids: number[], nodes: FlatCategory[]) => void;
  fallbackNames?: string[];
  label?: string;
  ariaLabel?: string;
  maxValues?: number;
};

export const CategoryTreeMultiSelect = ({
  value,
  onChange,
  fallbackNames,
  label,
  ariaLabel,
  maxValues,
}: CategoryTreeMultiSelectProps) => {
  const { nodes, byValue, loading, failed } = useCategoryTree();
  const matchedRef = useRef(false);

  useEffect(() => {
    if (matchedRef.current || loading || value.length > 0 || !fallbackNames?.length) return;
    matchedRef.current = true;
    const found = fallbackNames
      .map((name) => matchCategory(nodes, name))
      .filter((n): n is FlatCategory => !!n);
    if (found.length > 0) onChange(found.map((n) => n.id), found);
  }, [loading, nodes, value, fallbackNames, onChange]);

  return (
    <MultiSelect
      label={label}
      aria-label={ariaLabel}
      placeholder={
        value.length > 0
          ? "Thêm danh mục"
          : statusPlaceholder(loading, failed, fallbackNames?.join(", "))
      }
      data={nodes.map((n) => ({
        value: n.value,
        label: n.level === 0 ? n.name : `${n.name} (${levelLabel(n).toLowerCase()})`,
      }))}
      filter={pathFilter(byValue)}
      value={value.map(String)}
      onChange={(vals) => {
        const picked = vals
          .map((v) => byValue.get(v))
          .filter((n): n is FlatCategory => !!n);
        onChange(
          picked.map((n) => n.id),
          picked
        );
      }}
      renderOption={({ option, checked }) =>
        renderTreeOption(byValue.get(option.value), option.label, checked)
      }
      searchable
      clearable
      maxValues={maxValues}
      disabled={loading}
      error={failed ? "Không gọi được API danh mục, thử tải lại trang" : undefined}
      nothingFoundMessage="Không có danh mục khớp"
      maxDropdownHeight={320}
      comboboxProps={{ withinPortal: true, keepMounted: false }}
    />
  );
};
